import React, { useEffect, useState } from "react";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('component_password_confirm_modal');

interface PasswordConfirmModalProps {
  isOpen: boolean;
  title: string;
  label?: string;
  confirmLabel?: string;
  revealedLabel?: string;
  // Xac thuc lai mat khau tai khoan dang dang nhap va tra ve gia tri nhay cam can hien thi (VD
  // mat khau bao ve file ZIP giao nha in) — nem loi (message tieng Viet) neu mat khau sai.
  onSubmit: (password: string) => Promise<string>;
  onClose: () => void;
}

// Popup dung chung cho moi luong "nhap lai mat khau tai khoan de xem du lieu nhay cam" — sau khi
// xac thuc thanh cong, hien thi luon gia tri tra ve ngay trong modal (khong luu lai o dau khac,
// dong modal la mat, phai nhap lai mat khau neu muon xem lai lan nua).
const PasswordConfirmModal: React.FC<PasswordConfirmModalProps> = ({
  isOpen,
  title,
  label = st('mat_khau_tai_khoan_text'),
  confirmLabel = st('xac_nhan_text'),
  revealedLabel = st('mat_khau_text'),
  onSubmit,
  onClose,
}) => {
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setPassword("");
      setRevealedValue(null);
    }
  }, [isOpen]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!password.trim() || submitting) return;

    setSubmitting(true);
    try {
      const value = await onSubmit(password.trim());
      setRevealedValue(value);
    } catch (error: any) {
      toast.error(error?.message || st('xac_thuc_mat_khau_that_bai_message'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered>
      <ModalHeader className="bg-light p-3" toggle={onClose}>
        {title}
      </ModalHeader>
      {revealedValue === null ? (
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup className="mb-0">
              <Label>{label}</Label>
              <Input
                type="password"
                required
                autoFocus
                value={password}
                data-action-code="input-password-confirm"
                data-action-name={label}
                disabled={!PageActionPermission.canEnable(location.pathname, "input-password-confirm")}
                onChange={(event) => setPassword(event.target.value)}
              />
            </FormGroup>
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button color="light" onClick={onClose} disabled={submitting}>
                {st('dong_button')}
              </Button>
              {PageActionPermission.canDisplay(location.pathname, "button-password-confirm") && (
                <Button
                  color="primary"
                  type="submit"
                  data-action-code="button-password-confirm"
                  data-action-name={confirmLabel}
                  disabled={submitting}
                >
                  {submitting && (
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  )}
                  {confirmLabel}
                </Button>
              )}
            </div>
          </div>
        </Form>
      ) : (
        <>
          <ModalBody>
            <Label>{revealedLabel}</Label>
            <Input type="text" readOnly value={revealedValue} className="fw-semibold" />
          </ModalBody>
          <div className="modal-footer">
            <div className="hstack gap-2 justify-content-end">
              <Button color="light" onClick={onClose}>
                {st('dong_button')}
              </Button>
            </div>
          </div>
        </>
      )}
    </Modal>
  );
};

export default PasswordConfirmModal;
